import React, { useState } from "react";

/*
 * StoryShareCard — v5  (1:1, DNH logo header, filled summary)
 * 800×800 square captured as PNG by html-to-image.
 *
 * Row layout (top → bottom):
 *   1. Crimson top bar
 *   2. LOGO ROW  — DNH logo (img) · dnh.ai URL
 *   3. Hairline
 *   4. METADATA ROW — ● signal · CATEGORY · DATE · COUNTRY · EDITED BY DNH AI
 *   5. Headline (large serif)
 *   6. Full-width image  (NO category badge on image)
 *   7. Intelligence Summary label + body text (20 px serif)
 *   8. "READ THE FULL BRIEF AT DNH.AI →" CTA
 *   9. Source footer
 *  10. Crimson bottom bar
 *
 * Inline styles only — html-to-image cannot read Tailwind / CSS vars.
 */

// ── Design tokens ────────────────────────────────────────────────────────────
const C = {
  paper:    "#FAF8F4",
  ink:      "#111111",
  ink2:     "#2E2E2E",
  muted:    "#6B6B6B",
  hairline: "#E0DBD4",
  crimson:  "#F15A2C",
};

const CONF_COLOR = { high: "#F15A2C", moderate: "#111111", developing: "#6B6B6B" };
const confColor  = (c) => CONF_COLOR[(c || "").toLowerCase()] || "#6B6B6B";
const confLabel  = (c) => (c || "").toUpperCase() || "SIGNAL";

const CAT_GRADIENTS = {
  politics:      "linear-gradient(160deg, #1a1a2e 0%, #0f3460 100%)",
  business:      "linear-gradient(160deg, #0d1b2a 0%, #1e3a5f 100%)",
  technology:    "linear-gradient(160deg, #0f0c29 0%, #24243e 100%)",
  science:       "linear-gradient(160deg, #093028 0%, #237a57 100%)",
  health:        "linear-gradient(160deg, #1a1a2e 0%, #4a1942 100%)",
  sports:        "linear-gradient(160deg, #1c1c1c 0%, #4a0e0e 100%)",
  entertainment: "linear-gradient(160deg, #200122 0%, #6f0000 100%)",
  environment:   "linear-gradient(160deg, #0a2e0a 0%, #1a5c1a 100%)",
  default:       "linear-gradient(160deg, #1a1a1a 0%, #2d2d2d 100%)",
};
const catGradient = (slug) =>
  CAT_GRADIENTS[(slug || "").split("-")[0].toLowerCase()] || CAT_GRADIENTS.default;

// ── Component ─────────────────────────────────────────────────────────────────
const StoryShareCard = React.forwardRef(({ story }, ref) => {
  const [imgError, setImgError] = useState(false);
  if (!story) return null;

  const headline    = story.headline || story.title || "";
  const summary     = story.summary  || story.description || "";
  const catSlug     = story.category_slug || "";
  const catLabel    = (story.category_name || catSlug).replace(/-/g, " ").toUpperCase();
  const source      = story.source_name || "";
  const country     = (story.origin_country || "").replace(/-/g, " ").toUpperCase();
  const conf        = story.confidence || "moderate";
  const sourceCount = story.source_count || null;

  // Image credit — only show when genuinely different from the news source:
  //   • null / same as source_name → omit (no redundancy)
  //   • "DNH AI" (AI-generated) → show as-is
  //   • Different publisher → show it
  const rawCredit  = story.image_source?.publisher || null;
  const imageCredit =
    !rawCredit ? null
    : rawCredit.toLowerCase() === source.toLowerCase() ? null
    : rawCredit;

  const dateStr = new Date().toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  }).toUpperCase();

  // Use original image URL directly with crossOrigin="anonymous"
  // Publishers send Access-Control-Allow-Origin: * so html-to-image
  // can inline via browser fetch — no proxy needed.
  const hasImage = !!story.image_url && !imgError;
  const gradient = catGradient(catSlug);

  const headlineSize =
    headline.length > 110 ? "26px" :
    headline.length > 75  ? "30px" :
    headline.length > 50  ? "34px" : "38px";

  return (
    <div
      aria-hidden="true"
      style={{
        position:      "fixed",
        left:          "-9999px",
        top:           "0",
        width:         "800px",
        height:        "800px",
        overflow:      "hidden",
        pointerEvents: "none",
        zIndex:        -1,
      }}
    >
      <div
      ref={ref}
      data-export-photo-status={story.image_url ? (imgError ? "unavailable" : "available") : "none"}
        style={{
          width:         "800px",
          height:        "800px",
          display:       "flex",
          flexDirection: "column",
          background:    C.paper,
          fontFamily:    "'Georgia', 'Times New Roman', serif",
          overflow:      "hidden",
        }}
      >
        {/* ── 1. Crimson top bar ── */}
        <div style={{ height: "5px", background: C.crimson, flexShrink: 0 }} />

        {/* ══════════════════════════════════════════
            2. LOGO ROW — DNH logo · dnh.ai
            ══════════════════════════════════════════ */}
        <div style={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          padding:        "10px 24px 8px",
          flexShrink:     0,
          position:       "relative",
        }}>
          {/* DNH logo — user-provided PNG, served from same origin */}
          <img
            src="/dnh-logo-h.png"
            alt="Daily News Highlights"
            crossOrigin="anonymous"
            style={{ height: "44px", width: "auto", display: "block" }}
          />

          {/* Centre — full website URL */}
          <span style={{
            fontFamily:    "monospace",
            fontSize:      "10px",
            letterSpacing: "0.12em",
            color:         C.muted,
            textTransform: "lowercase",
            position:      "absolute",
            left:          "50%",
            transform:     "translateX(-50%)",
          }}>
            www.dailynewshighlights.com
          </span>

          {/* Right — short URL */}
          <span style={{
            fontFamily:    "monospace",
            fontSize:      "11px",
            letterSpacing: "0.16em",
            color:         C.crimson,
            textTransform: "uppercase",
            fontWeight:    "700",
          }}>
            dnh.ai
          </span>
        </div>

        {/* Hairline under logo row */}
        <div style={{
          height: "1px", background: C.hairline,
          flexShrink: 0, margin: "0 24px",
        }} />

        {/* ══════════════════════════════════════════
            3. METADATA ROW — signal · CATEGORY · DATE · COUNTRY · EDITED BY DNH AI
            ══════════════════════════════════════════ */}
        <div style={{
          display:    "flex",
          alignItems: "center",
          padding:    "9px 24px 8px",
          flexShrink: 0,
          gap:        "10px",
          flexWrap:   "nowrap",
          overflow:   "hidden",
        }}>
          {/* Signal */}
          <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            <div style={{
              width: "6px", height: "6px", borderRadius: "50%",
              background: confColor(conf),
            }} />
            <span style={{
              fontFamily: "monospace", fontSize: "9px",
              letterSpacing: "0.15em", color: confColor(conf),
              textTransform: "uppercase", fontWeight: "700",
            }}>
              {confLabel(conf)}
            </span>
          </div>

          <span style={{ color: C.hairline, fontSize: "8px", flexShrink: 0 }}>|</span>

          {/* Category */}
          {catLabel && (
            <span style={{
              fontFamily: "monospace", fontSize: "9px",
              letterSpacing: "0.12em", color: C.ink2,
              textTransform: "uppercase", flexShrink: 0,
            }}>
              {catLabel}
            </span>
          )}

          <span style={{ color: C.hairline, fontSize: "8px", flexShrink: 0 }}>|</span>

          {/* Date */}
          <span style={{
            fontFamily: "monospace", fontSize: "9px",
            letterSpacing: "0.1em", color: C.muted,
            textTransform: "uppercase", flexShrink: 0,
          }}>
            {dateStr}
          </span>

          {/* Country */}
          {country && (
            <>
              <span style={{ color: C.hairline, fontSize: "8px", flexShrink: 0 }}>|</span>
              <span style={{
                fontFamily: "monospace", fontSize: "9px",
                letterSpacing: "0.12em", color: C.crimson,
                textTransform: "uppercase", fontWeight: "700", flexShrink: 0,
              }}>
                {country}
              </span>
            </>
          )}

          {/* Spacer */}
          <div style={{ flex: 1, minWidth: "4px" }} />

          {/* Edited by DNH AI */}
          <div style={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
            <div style={{ width: "5px", height: "5px", borderRadius: "50%", background: C.crimson }} />
            <span style={{
              fontFamily: "monospace", fontSize: "8.5px",
              letterSpacing: "0.1em", color: C.ink2, textTransform: "uppercase",
            }}>
              EDITED BY DNH AI
            </span>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            4. HEADLINE
            ══════════════════════════════════════════ */}
        <div style={{ padding: "4px 24px 12px", flexShrink: 0 }}>
          <h1 style={{
            fontFamily:    "'Georgia', 'Times New Roman', serif",
            fontSize:      headlineSize,
            fontWeight:    "400",
            lineHeight:    "1.2",
            letterSpacing: "-0.015em",
            color:         C.ink,
            margin:        "0",
            overflow:      "hidden",
            display:       "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
          }}>
            {headline}
          </h1>
        </div>

        {/* ══════════════════════════════════════════
            5. STORY IMAGE — no category badge overlay
            ══════════════════════════════════════════ */}
        <div style={{
          width:      "800px",
          height:     "330px",
          flexShrink: 0,
          background: gradient,
          position:   "relative",
          overflow:   "hidden",
        }}>
          {hasImage && (
            <img
              src={story.image_url}
              alt=""
              crossOrigin="anonymous"
              onError={() => setImgError(true)}
              style={{
                position: "absolute", inset: "0",
                width: "100%", height: "100%",
                objectFit: "cover", display: "block",
              }}
            />
          )}
          {hasImage && (
            <div style={{
              position: "absolute", bottom: "0", left: "0", right: "0",
              height: "50px",
              background: "linear-gradient(to top, rgba(0,0,0,0.25) 0%, transparent 100%)",
            }} />
          )}
          {!hasImage && catLabel && (
            <div style={{
              position: "absolute", inset: "0",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{
                fontFamily: "'Georgia', serif", fontSize: "22px",
                color: "rgba(255,255,255,0.4)", textTransform: "capitalize",
              }}>
                {catLabel.toLowerCase()}
              </span>
            </div>
          )}
        </div>

        {/* ══════════════════════════════════════════
            6. INTELLIGENCE SUMMARY
            ══════════════════════════════════════════ */}
        <div style={{
          padding:       "14px 24px 10px",
          flex:          1,
          overflow:      "hidden",
          display:       "flex",
          flexDirection: "column",
          gap:           "9px",
        }}>
          {/* Label + source count */}
          <div style={{ display: "flex", alignItems: "center", gap: "8px", flexShrink: 0 }}>
            <span style={{
              fontFamily: "monospace", fontSize: "8.5px",
              letterSpacing: "0.22em", color: C.crimson,
              textTransform: "uppercase", fontWeight: "700",
            }}>
              Intelligence Summary
            </span>
            {sourceCount && sourceCount > 1 && (
              <>
                <div style={{ width: "3px", height: "3px", borderRadius: "50%", background: C.muted }} />
                <span style={{
                  fontFamily: "monospace", fontSize: "8.5px",
                  letterSpacing: "0.1em", color: C.muted, textTransform: "uppercase",
                }}>
                  {sourceCount} publishers
                </span>
              </>
            )}
          </div>

          {/* Summary body — 20px serif, fills space */}
          <p style={{
            fontFamily:      "'Georgia', 'Times New Roman', serif",
            fontSize:        "19px",
            lineHeight:      "1.68",
            color:           C.ink2,
            margin:          "0",
            overflow:        "hidden",
            display:         "-webkit-box",
            WebkitLineClamp: 5,
            WebkitBoxOrient: "vertical",
          }}>
            {summary}
          </p>

          {/* Spacer pushes CTA to bottom */}
          <div style={{ flex: 1 }} />

          {/* CTA */}
          <div style={{ display: "flex", alignItems: "center", gap: "5px", flexShrink: 0 }}>
            <span style={{
              fontFamily: "monospace", fontSize: "8.5px",
              letterSpacing: "0.14em", textTransform: "uppercase",
              color: C.crimson, fontWeight: "700",
            }}>
              Read the full brief at dnh.ai
            </span>
            <span style={{ color: C.crimson, fontSize: "10px" }}>→</span>
          </div>
        </div>

        {/* ══════════════════════════════════════════
            7. SOURCE FOOTER
            ══════════════════════════════════════════ */}
        <div style={{
          display:        "flex",
          alignItems:     "center",
          justifyContent: "space-between",
          padding:        "9px 24px 10px",
          borderTop:      `1px solid ${C.hairline}`,
          flexShrink:     0,
          position:       "relative",
        }}>
          {/* Left: news source */}
          <span style={{
            fontFamily: "monospace", fontSize: "8.5px",
            letterSpacing: "0.12em", color: C.muted, textTransform: "uppercase",
          }}>
            {source ? `via ${source}` : ""}
          </span>

          {/* Centre: image credit */}
          {imageCredit && (
            <span style={{
              fontFamily: "monospace", fontSize: "8px",
              letterSpacing: "0.1em", color: C.muted,
              textTransform: "uppercase",
              position: "absolute", left: "50%", transform: "translateX(-50%)",
            }}>
              Photo: {imageCredit}
            </span>
          )}

          {/* Right: dnh.ai */}
          <span style={{
            fontFamily: "monospace", fontSize: "8.5px",
            letterSpacing: "0.18em", color: C.muted, textTransform: "uppercase",
          }}>
            dnh.ai
          </span>
        </div>

        {/* ── 8. Crimson bottom bar ── */}
        <div style={{ height: "5px", background: C.crimson, flexShrink: 0 }} />
      </div>
    </div>
  );
});

StoryShareCard.displayName = "StoryShareCard";
export default StoryShareCard;
export { StoryShareCard };
